import React, { useState } from "react";
import { CheckCircle2, Globe } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { NxBadge } from "@/components/ui/NxBadge";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { apiPatch } from "@/lib/api";
import pt from "@/lib/i18n";
import { motion, AnimatePresence } from "framer-motion";

const ProfilePage = () => {
    const { user, updateProfile } = useAuth();
    const { lang, setLang } = useLanguage();
    const [form, setForm] = useState({
        full_name: user?.full_name || "",
        phone: user?.phone || "",
        preferred_language: user?.preferred_language || lang,
    });
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileOk, setProfileOk] = useState(false);
    const [profileErr, setProfileErr] = useState("");

    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [savingPw, setSavingPw] = useState(false);
    const [pwOk, setPwOk] = useState(false);
    const [pwErr, setPwErr] = useState("");

    const saveProfile = async (e) => {
        e.preventDefault();
        setProfileErr("");
        setProfileOk(false);
        setSavingProfile(true);
        try {
            await updateProfile(form);
            setProfileOk(true);
            setTimeout(() => setProfileOk(false), 2400);
        } catch (err) {
            setProfileErr(err?.response?.data?.errors?.[0]?.message || pt.errors.generic);
        } finally {
            setSavingProfile(false);
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        setPwErr("");
        setPwOk(false);
        if (pw.next !== pw.confirm) {
            setPwErr(pt.profile.passwordMismatch);
            return;
        }
        setSavingPw(true);
        try {
            await apiPatch("/auth/me/password", {
                current_password: pw.current,
                new_password: pw.next,
            });
            setPw({ current: "", next: "", confirm: "" });
            setPwOk(true);
            setTimeout(() => setPwOk(false), 2400);
        } catch (err) {
            setPwErr(err?.response?.data?.errors?.[0]?.message || pt.errors.generic);
        } finally {
            setSavingPw(false);
        }
    };

    return (
        <div data-testid="profile-root">
            <PageHeader title={pt.profile.title} subtitle={pt.profile.subtitle} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Identity card */}
                <GlassCard padding="p-7" className="lg:col-span-1" testId="profile-identity">
                    <div className="flex flex-col items-center text-center">
                        <NxAvatar name={user?.full_name} size="xl" />
                        <h3 className="mt-3 font-display text-xl text-[color:var(--slate-deep)]">
                            {user?.full_name}
                        </h3>
                        <p className="font-mono text-xs text-[color:var(--slate-mid)] mt-1">
                            {user?.email}
                        </p>
                        <div className="mt-3">
                            <NxBadge variant="copper">
                                {pt.users.roleLabels[user?.role] || user?.role}
                            </NxBadge>
                        </div>
                    </div>
                </GlassCard>

                {/* Profile form */}
                <GlassCard padding="p-7" className="lg:col-span-2" testId="profile-form">
                    <h3 className="font-display text-xl text-[color:var(--slate-deep)] mb-4">
                        {pt.profile.personalInfo}
                    </h3>
                    <form onSubmit={saveProfile} className="space-y-4">
                        <GlassInput
                            label={pt.users.form.fullName}
                            value={form.full_name}
                            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                            required
                            testId="profile-name"
                        />
                        <GlassInput
                            label={pt.users.form.phone}
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            testId="profile-phone"
                        />
                        <div>
                            <label className="flex items-center gap-1.5 text-xs font-medium text-[color:var(--slate-mid)] mb-1.5 tracking-wide uppercase">
                                <Globe size={12} className="text-[color:var(--copper)]" />
                                {pt.profile.language || "Idioma da interface"}
                            </label>
                            <select
                                value={form.preferred_language}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setForm({ ...form, preferred_language: v });
                                    // Apply instantly to the UI via LanguageContext
                                    setLang(v);
                                }}
                                className="glass-input"
                                data-testid="profile-language"
                            >
                                <option value="pt">{pt.profile.portuguese || "Português (pt-AO)"}</option>
                                <option value="en">{pt.profile.english || "English"}</option>
                            </select>
                            <p className="text-[11px] font-body text-[color:var(--slate-light)] mt-1.5">
                                {pt.profile.languageHelp || "Afecta navegação, painel e administração. Conteúdo de módulos permanece em Português."}
                            </p>
                        </div>
                        {profileErr && (
                            <p className="text-sm text-[color:var(--error)]">{profileErr}</p>
                        )}
                        <div className="flex justify-end gap-3 items-center pt-2">
                            <AnimatePresence>
                                {profileOk && (
                                    <motion.span
                                        initial={{ opacity: 0, x: 10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="inline-flex items-center gap-1.5 text-sm text-[color:var(--success)] font-body"
                                    >
                                        <CheckCircle2 size={15} /> {pt.profile.savedOk}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                            <NxButton
                                variant="copper"
                                type="submit"
                                loading={savingProfile}
                                testId="profile-save"
                            >
                                {pt.profile.saveProfile}
                            </NxButton>
                        </div>
                    </form>
                </GlassCard>

                {/* Password */}
                <GlassCard padding="p-7" className="lg:col-span-3" testId="password-form">
                    <h3 className="font-display text-xl text-[color:var(--slate-deep)] mb-4">
                        {pt.profile.changePassword}
                    </h3>
                    <form
                        onSubmit={savePassword}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end"
                    >
                        <GlassInput
                            label={pt.profile.currentPassword}
                            togglePassword
                            required
                            value={pw.current}
                            onChange={(e) => setPw({ ...pw, current: e.target.value })}
                            testId="pw-current"
                        />
                        <GlassInput
                            label={pt.profile.newPassword}
                            togglePassword
                            required
                            minLength={8}
                            value={pw.next}
                            onChange={(e) => setPw({ ...pw, next: e.target.value })}
                            testId="pw-next"
                        />
                        <GlassInput
                            label={pt.profile.confirmNew}
                            togglePassword
                            required
                            minLength={8}
                            value={pw.confirm}
                            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                            testId="pw-confirm"
                        />
                        <div className="md:col-span-3 flex justify-end items-center gap-3">
                            <AnimatePresence>
                                {pwOk && (
                                    <motion.span
                                        initial={{ opacity: 0, x: 10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="inline-flex items-center gap-1.5 text-sm text-[color:var(--success)] font-body"
                                    >
                                        <CheckCircle2 size={15} /> {pt.profile.passwordOk}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                            {pwErr && <span className="text-sm text-[color:var(--error)]">{pwErr}</span>}
                            <NxButton
                                variant="copper"
                                type="submit"
                                loading={savingPw}
                                testId="pw-submit"
                            >
                                {pt.profile.savePassword}
                            </NxButton>
                        </div>
                    </form>
                </GlassCard>
            </div>
        </div>
    );
};

export default ProfilePage;
